import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHistory, useRouteMatch } from 'react-router';
import { ERouterPath } from '../../features/router/ERouterPath';
import './SplitSubLayout.scss';

export interface ISplitLayoutItem {
  element: JSX.Element;
  mobileBackButtonElement?: JSX.Element;
  routerPath?: ERouterPath|string;
  matchPaths?: ERouterPath[];
  selectedClassName?: string;
  className?: string;
  onSelect?: () => void;
}

interface ISplitSubLayoutProps {
  items: ISplitLayoutItem[];
  /**
   * By default the user will be redirected to the first item with a routerPath
   * when none of the items is currently selected. This behavior can be disabled
   * by setting this prop to true.
   */
  skipLoadRedirectWhenNoItemSelected?: boolean;
  /**
   * By default when the list contains only one item with a routerPath the user is
   * redirected directly to that route.
   * Disable this behavior by setting this to false.
   */
  skipLoadRedirectWhenOnlyOneItem?: boolean;
  /**
   * This is the path used by the mobile back button, it should land on the split
   * layout without an item selected.
   */
  rootRouterPath?: ERouterPath|string;
  hideSidebarAndBackButton?: boolean;
  noPadding?: boolean;
  className?: string;
  onBack?: () => void;
}

export function SplitSubLayout({ className, items, skipLoadRedirectWhenNoItemSelected, skipLoadRedirectWhenOnlyOneItem, rootRouterPath, hideSidebarAndBackButton, noPadding, onBack, children }: PropsWithChildren<ISplitSubLayoutProps>): JSX.Element {
  const history = useHistory();
  const match = useRouteMatch();
  const [currentlySelected, setCurrentlySelected] = useState<ISplitLayoutItem|undefined>(undefined);
  const [mobileMode, setMobileMode] = useState<boolean>(true); // Always assume we are in mobile mode to prevent the loadRedirect from triggering before we know the users mode

  const resizeUpdate = useCallback(() => {
    const { innerWidth } = window;
    const isMobileMode = innerWidth <= 767; // 767 = $tablet-breakpoint in _library.scss
    if (isMobileMode !== mobileMode) {
      setMobileMode(isMobileMode);
    }
  }, [mobileMode]);

  useEffect(() => {
    resizeUpdate();
    window.addEventListener('resize', resizeUpdate);
    return () => window.removeEventListener('resize', resizeUpdate);
  }, [resizeUpdate]);

  const handleClickItem = useCallback((item: ISplitLayoutItem, { doNotPushHistory }: { doNotPushHistory?: boolean } = {}): void => {
    setCurrentlySelected(item);

    if (item.onSelect) {
      item.onSelect();
    }

    if (item.routerPath) {
      if (doNotPushHistory) {
        history.replace(item.routerPath);
      } else {
        history.push(item.routerPath);
      }
    }
  }, [history]);

  const handleClickBack = useCallback((): void => {
    if (onBack) {
      onBack();
    }
    if (rootRouterPath) {
      history.push(rootRouterPath);
    } else {
      history.goBack();
    }
  }, [history, rootRouterPath, onBack]);

  const isSelected = useCallback((item: ISplitLayoutItem): boolean => item.routerPath === match.url, [match]);
  const isPathMatch = useCallback((item: ISplitLayoutItem): boolean => item.routerPath === match.url || (item.matchPaths ?? []).includes(match.path as ERouterPath), [match]);

  useEffect(() => {
    let selectedItem: ISplitLayoutItem|undefined = undefined;
    let matchedItem: ISplitLayoutItem|undefined = undefined;
    let firstItemWithRouterPath: ISplitLayoutItem|undefined = undefined;
    let navigateToFirstItemWithRouterPath = true;
    let itemsWithRouterPath = 0;
    for (const item of items) {
      if (!item.routerPath) {
        continue;
      }

      itemsWithRouterPath++;

      if (!firstItemWithRouterPath) {
        firstItemWithRouterPath = item;
      }

      if (isSelected(item)) {
        selectedItem = item;
        navigateToFirstItemWithRouterPath = false;
      }

      if (isPathMatch(item)) {
        matchedItem = item;
      }
    }

    if (selectedItem && !currentlySelected) {
      // The page was loaded on the currently selected item, but since it was a load the item is not yet active
      handleClickItem(selectedItem, { doNotPushHistory: true });
    } else if (!mobileMode && !skipLoadRedirectWhenNoItemSelected && navigateToFirstItemWithRouterPath && firstItemWithRouterPath) {
      // Redirect the user to the first item with a routerPath when none is selected.
      // Don't do this if the user is in mobile mode, then we show the sidebar and let the user pick where they wat to go.
      handleClickItem(firstItemWithRouterPath, { doNotPushHistory: true });
    } else if (!skipLoadRedirectWhenOnlyOneItem && !selectedItem && itemsWithRouterPath === 1 && firstItemWithRouterPath) {
      // There is only one item, redirect
      handleClickItem(firstItemWithRouterPath, { doNotPushHistory: true });
    } else if (matchedItem) {
      // We did not have an selected item, but we do have a items that mached one of the matchPaths, set it as selected, but do not route
      setCurrentlySelected(matchedItem);
    }
  }, [items, isSelected, isPathMatch, handleClickItem, skipLoadRedirectWhenNoItemSelected, skipLoadRedirectWhenOnlyOneItem, currentlySelected, mobileMode]);

  useEffect(() => {
    // The url changed, lets validate
    if (currentlySelected && !isSelected(currentlySelected) && !isPathMatch(currentlySelected)) {
      setCurrentlySelected(undefined);
    }
  }, [match.url, isSelected, isPathMatch, currentlySelected]);

  const fullClassName = classNames(
    'split-layout',
    className,
    {
      'split-layout--mobile': mobileMode,
      'split-layout--no-item-selected': !currentlySelected,
    },
  );

  const renderSidebar = () => <div className={classNames('split-layout__item-column', { 'split-layout__item-column--no-padding': noPadding })}>
    {
      items.map((item, index) => {
        if (item.routerPath || item.onSelect) {
          const selected = isSelected(item);
          return <button
            key={index}
            className={classNames('split-layout__item', 'split-layout__item--button', item.className, { 'split-layout__item--selected': selected && !item.selectedClassName, 'split-layout__item--mobile': mobileMode }, selected && item.selectedClassName)}
            onClick={() => handleClickItem(item)}
          >
            {item.element}
          </button>;
        }
        return <div key={index} className={classNames('split-layout__item', item.className)}>{item.element}</div>;
      })
    }
  </div>;

  const showSideBar = (items.length > 0) && (!hideSidebarAndBackButton || !currentlySelected); // Always show the sidebar when nothing is selected, no matter hideSidebarAndBackButton
  return (
    <div className={fullClassName}>
      { showSideBar && renderSidebar() }
      <div className={classNames('split-layout__content-column', { 'split-layout__content-column--no-padding': noPadding })}>
        { (currentlySelected && !hideSidebarAndBackButton) && <button className="split-layout__mobile-back-button" onClick={handleClickBack} aria-label="Terug">{currentlySelected.mobileBackButtonElement ?? currentlySelected.element}</button> }
        {children}
      </div>
    </div>
  );
}
